import type { FC } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAtom } from 'jotai';

import type { DownloadState } from 'src/constants/downloadManager';
import type { DownloadGroups, ExportDownload } from 'src/types/downloadManager';
import { chatbotVisibilityState } from 'src/components/chatbot/atoms';
import { ChatbotState } from 'src/components/chatbot/constants';

import { useDownloadManager } from '../DownloadManagerContext';

import DeleteDownloadsModal from './components/DeleteDownloadsModal';
import DownloadManagerSection from './components/DownloadManagerSection';

import { styles } from './index.styles';

const DownloadManager: FC = () => {
  const { t } = useTranslation('downloadManager');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBouncingArrow, setShowBouncingArrow] = useState(false);
  const [arrowShown, setArrowShown] = useState(false);
  const isOpenRef = useRef(false);
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const [chatbotVisibility, setChatbotState] = useAtom(chatbotVisibilityState);

  const {
    downloads,
    unavailableDownloads,
    showDownloadManager,
    hideAllDownloads,
    hideExport,
  } = useDownloadManager();

  const groupedDownloads = useMemo(() => {
    const groups: DownloadGroups = {} as DownloadGroups;
    downloads.forEach((download) => {
      const { status, category } = download;
      if (!groups[status]) {
        groups[status] = {};
      }
      if (!groups[status][category]) {
        groups[status][category] = [];
      }
      groups[status][category].push(download);
    });
    return groups;
  }, [downloads]);

  const unavailableReportsByCase = useMemo(() => {
    const unavailableOpenCommentReports = unavailableDownloads.filter(
      (download) => download.category === 'OpenCommentReport',
    );
    const unavailableNonCommentReports = unavailableDownloads.filter(
      (download) => download.category !== 'OpenCommentReport',
    );

    return {
      OpenCommentReport: unavailableOpenCommentReports,
      Default: unavailableNonCommentReports,
    };
  }, [unavailableDownloads]);

  useEffect(() => {
    if (showDownloadManager && !arrowShown) {
      setShowBouncingArrow(true);
      setArrowShown(true);
      const timer = setTimeout(() => setShowBouncingArrow(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showDownloadManager]);

  const onClickHandler = () => {
    setShowBouncingArrow(false);
    isOpenRef.current = !isOpenRef.current;
    if (!upSm) {
      if (chatbotVisibility !== ChatbotState.DISABLED) {
        if (chatbotVisibility === ChatbotState.VISIBLE) {
          setChatbotState(ChatbotState.HIDDEN);
        } else {
          setChatbotState(ChatbotState.VISIBLE);
        }
      }
    }
  };

  const hideUnavailableDownloads = (unavailableDownloads: ExportDownload[]) => {
    unavailableDownloads.forEach(({ id }) => hideExport(id));
  };

  const onDeleteAll = useCallback(async () => {
    setIsDeleting(true);
    hideAllDownloads().then(() => {
      setIsDeleting(false);
      setShowDeleteModal(false);
    });
  }, [hideAllDownloads]);

  if (!showDownloadManager) return null;

  return (
    <Box>
      {unavailableDownloads && (
        <Snackbar
          onClose={() => hideUnavailableDownloads(unavailableDownloads)}
          open={unavailableDownloads.length > 0}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={styles.notification}
        >
          <SnackbarContent
            sx={styles.notificationContent}
            message={
              <Stack direction="row">
                <Stack direction="column" sx={{ justifyContent: 'center' }}>
                  <Stack sx={styles.notificationIcon}>
                    <ErrorRounded />
                  </Stack>
                </Stack>
                <Stack sx={{ gap: 3, ...styles.notificationText }}>
                  {Object.entries(unavailableReportsByCase).map(
                    ([caseName, reports], j) =>
                      reports.length > 0 ? (
                        <Box key={j}>
                          <Typography
                            variant="h6"
                            sx={{ fontSize: '18px', lineHeight: '1.75rem' }}
                          >
                            {t(`downloadManager:FilesNotAvailable.${caseName}`)}
                          </Typography>
                          {reports.map(({ name }, i, total) => (
                            <Box
                              key={i}
                              sx={{ display: 'inline-flex', maxWidth: '100%' }}
                            >
                              <Box
                                title={String(name)}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {String(name)}
                              </Box>
                              {i + 1 < total.length && (
                                <Box sx={{ flexShrink: 0 }}>,&nbsp;</Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : null,
                  )}
                </Stack>
              </Stack>
            }
            action={
              <IconButton
                size="large"
                aria-label="close"
                color="primary"
                onClick={() => hideUnavailableDownloads(unavailableDownloads)}
              >
                <CloseRounded color="primary" />
              </IconButton>
            }
          />
        </Snackbar>
      )}

      {showBouncingArrow && (
        <Button
          sx={styles.bouncingArrowButton}
          data-test-id="download-manager-arrow"
        >
          <Box sx={styles.bouncingArrowCircle}>
            <DownloadRounded sx={styles.bouncingArrow} />
          </Box>
        </Button>
      )}

      {showDownloadManager && (
        <Accordion sx={styles.root} onChange={onClickHandler}>
          <AccordionSummary
            sx={styles.summary}
            expandIcon={<KeyboardArrowUpRounded sx={styles.summaryArrow} />}
            data-testid="download-accordion-summary"
          >
            <DownloadRounded />
            <Typography variant="h5" sx={{ ml: 1.5 }}>
              {t('downloadManager:DownloadManager')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={styles.details}>
            <Box sx={styles.accordionContainer}>
              {Object.keys(groupedDownloads).map((status) => (
                <DownloadManagerSection
                  key={status}
                  title={t(`downloadManager:${status.toLowerCase()}`)}
                  downloads={groupedDownloads[status as DownloadState]}
                  state={status as DownloadState}
                />
              ))}
            </Box>

            <Button
              fullWidth
              sx={styles.endButton}
              onClick={() => setShowDeleteModal(true)}
            >
              {t('downloadManager:ExitDownloadManager')}
            </Button>
          </AccordionDetails>
        </Accordion>
      )}

      <DeleteDownloadsModal
        onClose={() => setShowDeleteModal(false)}
        showModal={showDeleteModal}
        onDelete={onDeleteAll}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default DownloadManager;
